import { ChevronDown, ChevronRight } from 'lucide-react'
import { type TriState } from '../../types'
import { useUpsellContext } from '../../hooks/useUpsellContext'

export function RequiredMark() {
  return <span className="text-blue-400 ml-1">*</span>
}

export function SectionHeader({
  title,
  subtitle,
}: {
  title: string
  subtitle?: string
}) {
  return (
    <div className="pt-2">
      <h4 className="text-base font-semibold text-[var(--text-primary)]">{title}</h4>
      {subtitle ? <p className="text-sm text-[var(--text-secondary)] mt-0.5">{subtitle}</p> : null}
    </div>
  )
}

export function TriStateRadio({
  name,
  value,
  onChange,
  disabled,
}: {
  name: string
  value: TriState
  onChange: (v: TriState) => void
  disabled: boolean
}) {
  const { mapLabel } = useUpsellContext()
  const options: { value: TriState; label: string }[] = [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
    { value: 'unknown', label: mapLabel("Don't know") },
  ]

  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => (
        <label
          key={opt.value}
          className={`px-3 py-2 rounded-lg border cursor-pointer text-sm transition-colors ${
            value === opt.value
              ? opt.value === 'unknown'
                ? 'border-amber-500 bg-amber-500/15 text-amber-400 font-medium'
                : 'border-primary bg-primary/10 text-[var(--text-primary)]'
              : 'border-[var(--border-color)] text-[var(--text-secondary)] hover:border-gray-600'
          } ${disabled ? (value === opt.value ? 'cursor-not-allowed' : 'opacity-40 cursor-not-allowed') : ''}`}
        >
          <input
            type="radio"
            name={name}
            value={opt.value}
            checked={value === opt.value}
            onChange={() => onChange(opt.value)}
            disabled={disabled}
            className="hidden"
          />
          {opt.value === 'unknown' && value === 'unknown' ? `⚠ ${opt.label}` : opt.label}
        </label>
      ))}
    </div>
  )
}

export const normalizeTriState = (value: unknown): TriState => {
  if (value === 'yes' || value === 'no' || value === 'unknown') return value
  if (value === true) return 'yes'
  if (value === false) return 'no'
  return 'unknown'
}

export const KYB_ENTITY_TYPE_SPLIT_FIELDS = [
  { key: 'soleTradersPercent', label: 'Sole Traders' },
  { key: 'ptyLtdPercent', label: 'PTY LTD' },
  { key: 'companyPercent', label: 'Company' },
  { key: 'partnershipPercent', label: 'Partnership' },
  { key: 'trustPercent', label: 'Trust' },
  { key: 'otherPercent', label: 'Other' },
]

export function PercentageSplitInput({
  fields,
  values,
  onChange,
  disabled,
}: {
  fields: Array<{ key: string; label: string }>
  values: Record<string, number>
  onChange: (key: string, value: number) => void
  disabled: boolean
}) {
  const total = Object.values(values).reduce((sum, v) => sum + (v || 0), 0)

  return (
    <div className="space-y-3">
      {fields.map((field) => (
        <div key={field.key} className="flex items-center gap-3">
          <span className="text-sm text-[var(--text-primary)] w-40 shrink-0">{field.label}</span>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min="0"
              max="100"
              value={values[field.key] || 0}
              onChange={(e) => onChange(field.key, Math.max(0, Math.min(100, parseInt(e.target.value) || 0)))}
              disabled={disabled}
              className="w-20 px-3 py-1.5 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg text-[var(--text-primary)] text-sm focus:outline-none focus:border-primary disabled:opacity-50"
            />
            <span className="text-sm text-[var(--text-muted)]">%</span>
          </div>
        </div>
      ))}
      <div className={`text-sm font-medium mt-2 ${total === 100 ? 'text-green-500' : total > 100 ? 'text-red-400' : 'text-yellow-500'}`}>
        Total: {total}%{total !== 100 && ' (should be 100%)'}
      </div>
    </div>
  )
}

export function DrawerSection({
  title,
  subtitle,
  isOpen,
  onToggle,
  children,
}: {
  id?: string
  title: string
  subtitle?: string
  isOpen: boolean
  onToggle: () => void
  children: React.ReactNode
}) {
  return (
    <div className="border border-[var(--border-color)] rounded-lg overflow-hidden">
      <button
        type="button"
        className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-[var(--bg-secondary)] hover:bg-[var(--bg-tertiary)] transition-colors text-left"
        onClick={onToggle}
      >
        <div>
          <h3 className="text-lg font-medium text-[var(--text-primary)]">{title}</h3>
          {subtitle && <p className="text-sm text-[var(--text-secondary)] mt-0.5">{subtitle}</p>}
        </div>
        {isOpen ? (
          <ChevronDown size={20} className="text-[var(--text-secondary)] flex-shrink-0" />
        ) : (
          <ChevronRight size={20} className="text-[var(--text-secondary)] flex-shrink-0" />
        )}
      </button>
      {isOpen && (
        <div className="px-4 py-5 space-y-6 border-t border-[var(--border-color)]">
          {children}
        </div>
      )}
    </div>
  )
}
